import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import { 
    Users, 
    Search, 
    Filter, 
    RefreshCw, 
    UserPlus, 
    UserCheck, 
    Clock, 
    LogOut,
    MessageSquare,
    ExternalLink,
    ChevronLeft,
    ChevronRight,
    ArrowRight,
    ChevronDown,
    ChevronUp,
    Calendar,
    Hash,
    Smile,
    Shield
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const Members = () => {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [expandedMemberId, setExpandedMemberId] = useState(null);
    
    const { data, isLoading } = useQuery({
        queryKey: ['members', page, search, statusFilter],
        queryFn: async () => {
            const resp = await client.get('/members', {
                params: { page, limit: 12, search, status: statusFilter }
            });
            return resp.data;
        },
        keepPreviousData: true
    });

    const convertToLeadMutation = useMutation({
        mutationFn: async (memberId) => (await client.post(`/members/${memberId}/convert-to-lead`)).data,
        onSuccess: (res) => {
            if (res.status === 'ok') {
                toast.success('Участник успешно добавлен в CRM как лид!');
                queryClient.invalidateQueries(['members']);
            } else if (res.status === 'already_exists') {
                toast.error('Этот участник уже есть в CRM.');
            }
        },
        onError: (err) => toast.error('Ошибка при конвертации: ' + (err.response?.data?.detail || err.message))
    });

    const members = data?.items || [];
    const totalPages = data?.pages || 1;

    const MemberRow = ({ member }) => {
        const isExpanded = expandedMemberId === member.id;
        
        return (
            <div className={`border-b border-white/5 transition-all duration-300 ${isExpanded ? 'bg-white/[0.03]' : 'hover:bg-white/[0.01]'}`}>
                {/* Header Section */}
                <div 
                    onClick={() => setExpandedMemberId(isExpanded ? null : member.id)}
                    className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-6 cursor-pointer group"
                >
                    <div className="flex items-center gap-5 flex-1">
                        <div className="w-14 h-14 rounded-2xl bg-zinc-800 border border-white/10 flex items-center justify-center text-zinc-500 overflow-hidden shadow-2xl group-hover:scale-105 transition-transform">
                            <img 
                                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(member.full_name || 'U')}&background=1a1a1a&color=3b82f6&bold=true`} 
                                alt={member.full_name} 
                                className="w-full h-full object-cover" 
                            />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-white italic tracking-tighter uppercase group-hover:text-blue-400 transition-colors">
                                {member.full_name || 'Неизвестно'}
                            </h3>
                            <div className="flex items-center gap-3 mt-1">
                                <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-1.5">
                                    <MessageSquare size={12} className="text-blue-500/50" />
                                    @{member.username || 'no-username'}
                                </span>
                                <span className="w-1 h-1 bg-zinc-800 rounded-full" />
                                <span className="text-[10px] font-mono text-zinc-600 bg-black/40 px-2 py-0.5 rounded-lg">ID: {member.telegram_id}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-8">
                        <div className="hidden lg:flex flex-col items-end">
                            <span className="text-[9px] font-black tracking-widest text-zinc-600 uppercase mb-1">Регистрация</span>
                            <span className="text-[11px] font-bold text-zinc-400 italic">
                                {format(new Date(member.joined_at), 'd MMM yyyy', { locale: ru })}
                            </span>
                        </div>

                        <div className={`px-5 py-2 rounded-xl text-[9px] font-black uppercase tracking-[0.2em] border transition-all ${
                            member.status === 'member' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 
                            member.status === 'left' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' :
                            'bg-zinc-800 text-zinc-500 border-white/5'
                        }`}>
                            {member.status === 'member' ? 'Подписан' : member.status === 'left' ? 'Отписался' : member.status}
                        </div>

                        <div className={`p-3 rounded-2xl transition-all ${isExpanded ? 'bg-blue-600 text-white' : 'bg-white/5 text-zinc-600'}`}>
                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                    </div>
                </div>

                {/* Expanded Section */}
                {isExpanded && (
                    <div className="px-8 pb-8 pt-2 animate-in slide-in-from-top-2 duration-300">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="space-y-6">
                                <section>
                                    <h4 className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <Hash size={12} /> Биография из канала
                                    </h4>
                                    <div className="bg-zinc-950/50 rounded-2xl p-5 border border-white/5 space-y-4">
                                        <div>
                                            <label className="text-[9px] font-black text-zinc-600 uppercase mb-1 block">Пол</label>
                                            <p className="text-sm text-zinc-300 font-bold">{member.gender === 'male' ? 'Мужской' : member.gender === 'female' ? 'Женский' : '—'}</p>
                                        </div>
                                        <div>
                                            <label className="text-[9px] font-black text-zinc-600 uppercase mb-1 block">День рождения</label>
                                            <p className="text-sm text-zinc-300 font-bold">{member.birthday || '—'}</p>
                                        </div>
                                    </div>
                                </section>
                            </div>

                            <div className="space-y-6">
                                <section>
                                    <h4 className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <Clock size={12} /> История подписки
                                    </h4>
                                    <div className="bg-zinc-950/50 rounded-2xl p-5 border border-white/5 space-y-4">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[10px] font-black text-zinc-600 uppercase">Вступил:</span>
                                            <span className="text-xs text-zinc-300 font-bold italic">{format(new Date(member.joined_at), 'PPP', { locale: ru })}</span>
                                        </div>
                                        {member.left_at && (
                                            <div className="flex justify-between items-center border-t border-white/5 pt-3">
                                                <span className="text-[10px] font-black text-rose-500/50 uppercase">Вышел:</span>
                                                <span className="text-xs text-rose-400 font-bold italic">{format(new Date(member.left_at), 'PPP', { locale: ru })}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between items-center border-t border-white/5 pt-3">
                                            <span className="text-[10px] font-black text-zinc-600 uppercase">В базе CRM:</span>
                                            <span className={`text-[10px] font-black uppercase ${member.is_lead ? 'text-emerald-500' : 'text-orange-500'}`}>
                                                {member.is_lead ? 'Уже добавлен' : 'Нет в CRM'}
                                            </span>
                                        </div>
                                    </div>
                                </section>
                            </div>

                            <div className="flex flex-col justify-end space-y-3">
                                <h4 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-1 flex items-center gap-2">
                                    <Shield size={12} /> Доступные действия
                                </h4>
                                {!member.is_lead ? (
                                    <button 
                                        onClick={() => convertToLeadMutation.mutate(member.id)}
                                        disabled={convertToLeadMutation.isLoading}
                                        className="w-full bg-blue-600 hover:bg-blue-500 text-white py-4 rounded-[1.5rem] font-black uppercase text-[11px] tracking-widest transition-all shadow-xl active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50"
                                    >
                                        <UserPlus size={18} /> Создать Лид в CRM
                                    </button>
                                ) : (
                                    <div className="w-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 py-4 rounded-[1.5rem] font-black uppercase text-[11px] tracking-widest flex items-center justify-center gap-3">
                                        <UserCheck size={18} /> Уже в работе CRM
                                    </div>
                                )}
                                {member.username && (
                                    <a 
                                        href={`https://t.me/${member.username}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="w-full bg-zinc-800 text-zinc-400 py-3 rounded-[1.5rem] font-black uppercase text-[9px] tracking-widest hover:text-white transition-all text-center flex items-center justify-center gap-2"
                                    >
                                        <ExternalLink size={14} /> Открыть профиль в TG
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="max-w-7xl mx-auto space-y-10 animate-in fade-in duration-500 pb-20">
            {/* Header section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
                <div className="space-y-1">
                    <h1 className="text-5xl font-black text-white tracking-tighter flex items-center gap-6 italic uppercase">
                        <div className="w-16 h-16 bg-emerald-600 rounded-[1.5rem] flex items-center justify-center shadow-2xl shadow-emerald-600/20">
                            <Users className="w-10 h-10 text-white" />
                        </div>
                        База Участников
                    </h1>
                    <p className="text-zinc-500 text-sm font-medium ml-1">Мониторинг аудитории Telegram каналов и анализ вовлеченности</p>
                </div>

                <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
                    <div className="flex-1 md:w-80 relative group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 group-focus-within:text-blue-500 transition-colors" size={18} />
                        <input 
                            type="text"
                            placeholder="Поиск по ID или Username..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            className="w-full bg-zinc-900/50 border border-white/5 text-white pl-12 pr-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest outline-none focus:border-blue-500 transition-all backdrop-blur-xl shadow-2xl"
                        />
                    </div>
                    
                    <button
                        onClick={async () => {
                            try {
                                const response = await client.get('/members/export', { responseType: 'blob' });
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `members_export_${new Date().toISOString().split('T')[0]}.csv`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                toast.success('CSV успешно выгружен');
                            } catch (err) {
                                toast.error('Ошибка выгрузки');
                            }
                        }}
                        className="p-4 bg-zinc-900 border border-white/5 text-zinc-400 rounded-2xl hover:bg-zinc-800 transition-all shadow-xl group"
                        title="Выгрузить данные"
                    >
                        <RefreshCw size={24} className="group-hover:rotate-180 transition-transform duration-500" />
                    </button>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex flex-wrap items-center gap-2 p-2 bg-zinc-900/50 rounded-[1.8rem] border border-white/10 w-fit backdrop-blur-md">
                {[
                    { label: 'Все', value: '' },
                    { label: 'Подписаны', value: 'member' },
                    { label: 'Отписались', value: 'left' },
                    { label: 'Бан', value: 'kicked' }
                ].map(tab => (
                    <button
                        key={tab.value}
                        onClick={() => { setStatusFilter(tab.value); setPage(1); setExpandedMemberId(null); }}
                        className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                            statusFilter === tab.value 
                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20' 
                            : 'text-zinc-500 hover:text-zinc-300'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Main List Display */}
            <div className="bg-zinc-900/30 border border-white/5 rounded-[3rem] overflow-hidden backdrop-blur-xl shadow-2xl">
                {isLoading ? (
                    <div className="divide-y divide-white/5">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="h-24 bg-white/[0.02] animate-pulse" />
                        ))}
                    </div>
                ) : members.length > 0 ? (
                    <div className="divide-y divide-white/5">
                        {members.map(member => (
                            <MemberRow key={member.id} member={member} />
                        ))}
                    </div>
                ) : (
                    <div className="py-40 text-center">
                        <div className="w-24 h-24 bg-zinc-800/50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 shadow-inner italic font-black text-5xl text-zinc-700">?</div>
                        <h3 className="text-2xl font-black text-zinc-500 uppercase tracking-tighter italic">База участников пуста</h3>
                        <p className="text-zinc-600 text-sm mt-3 max-w-sm mx-auto font-medium">Как только пользователи начнут вступать в привязанные Telegram-каналы, они автоматически появятся здесь.</p>
                    </div>
                )}
            </div>

            {/* Modern Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-3 py-6">
                    <button 
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="w-14 h-14 bg-zinc-900/80 border border-white/5 rounded-2xl flex items-center justify-center text-zinc-600 hover:text-white disabled:opacity-20 transition-all shadow-xl active:scale-90"
                    >
                        <ChevronLeft size={24} />
                    </button>
                    
                    <div className="flex items-center gap-2 p-2 bg-zinc-900/50 rounded-2xl border border-white/5">
                        {[...Array(totalPages)].map((_, i) => (
                            <button 
                                key={i}
                                onClick={() => setPage(i + 1)}
                                className={`w-10 h-10 rounded-xl text-[10px] font-black transition-all ${
                                    page === i + 1 
                                    ? 'bg-blue-600 text-white shadow-xl scale-110' 
                                    : 'text-zinc-600 hover:text-zinc-300'
                                }`}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>

                    <button 
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="w-14 h-14 bg-zinc-900/80 border border-white/5 rounded-2xl flex items-center justify-center text-zinc-600 hover:text-white disabled:opacity-20 transition-all shadow-xl active:scale-90"
                    >
                        <ChevronRight size={24} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default Members;
